import { matchPath } from 'react-router-dom';

import type {
  EmergencyIncident,
  EmergencyLocation,
  EmergencyMonitoringSession,
  EmergencyMonitoringWarningStage,
  EmergencyNotification,
  EmergencyResponder,
  EmergencyResponderState,
  EmergencyTimelineEntry,
} from '@src/models/interfaces/emergency.interface';
import { DateUtil } from '@src/packages/utils/dates';

import { EMERGENCY_TIMELINE_TYPES, ORGANIZATION_BASE_PATH, PATHS } from '../constants';

const INTERNAL_CLOSURE_OUTCOMES = new Set(['safe', 'selfSafe']);

export const getEmergencyClosureOutcomeLabel = (outcome?: string): string | undefined =>
  outcome && !INTERNAL_CLOSURE_OUTCOMES.has(outcome) ? outcome : undefined;

export const isEmergencyCommandCenterPath = (pathname: string): boolean => {
  if (
    matchPath(pathname, {
      path: `${ORGANIZATION_BASE_PATH}/${PATHS.COMMAND_CENTER}`,
      exact: true,
    })
  ) {
    return true;
  }

  const incidentMatch = matchPath<{ incidentId: string }>(pathname, {
    path: `${ORGANIZATION_BASE_PATH}/${PATHS.INCIDENT_DETAIL}`,
    exact: true,
  });
  return Boolean(
    incidentMatch &&
      !['history', 'monitoring', 'types', 'integrations'].includes(incidentMatch.params.incidentId),
  );
};

export type EmergencyFreshnessState = 'fresh' | 'warning' | 'stale';

export interface EmergencyIncidentFilters {
  divisionIds?: string[];
  search: string;
  urgency?: 'all' | 'unassigned' | 'overdue';
}

export type EmergencyDeadlineState = 'none' | 'upcoming' | 'due';

/**
 * Restricts records to a set of divisions, or passes them through untouched
 * when `divisionIds` is `null`.
 *
 * The emergency subscriptions issue a single unfiltered query for org-wide
 * readers instead of chunked `divisionId in [...]` queries, so the division
 * scope the operator picked in the sidebar has to be re-applied on the client.
 * `null` means "no restriction" and must be preserved as a distinct value from
 * an empty list, which means "nothing is in scope".
 */
export const filterByDivisionScope = <T extends { divisionId: string }>(
  records: T[],
  divisionIds: string[] | null,
): T[] => {
  if (divisionIds === null) return records;
  const scope = new Set(divisionIds);
  return records.filter(record => scope.has(record.divisionId));
};

export const getEmergencyMonitoringWarningStage = (
  session: EmergencyMonitoringSession,
  nowMs: number,
): EmergencyMonitoringWarningStage => {
  const emergencyAt = DateUtil.getTimestampMs(session.emergencyAt);
  const fullScreenWarningAt = DateUtil.getTimestampMs(session.fullScreenWarningAt);
  const standardWarningAt = DateUtil.getTimestampMs(session.standardWarningAt);

  if (emergencyAt !== null && nowMs >= emergencyAt) return 'overdue';
  if (fullScreenWarningAt !== null && nowMs >= fullScreenWarningAt) return 'fullScreen';
  if (standardWarningAt !== null && nowMs >= standardWarningAt) return 'standard';
  return 'normal';
};

/**
 * Whether a session needs an operator's attention: the worker has reached a
 * warning or the deadline has passed. There is no device heartbeat any more, so
 * a silent phone surfaces through the same warning stages. `degraded` only
 * appears on sessions stored before heartbeat tracking was removed.
 */
export const hasEmergencyMonitoringIssue = (
  session: EmergencyMonitoringSession,
  nowMs: number,
): boolean =>
  getEmergencyMonitoringWarningStage(session, nowMs) !== 'normal' ||
  session.state === 'degraded';

export const countEmergencyMonitoringIssues = (
  sessions: EmergencyMonitoringSession[],
  nowMs: number,
): number => sessions.filter(session => hasEmergencyMonitoringIssue(session, nowMs)).length;

export interface EmergencyMonitoringFilters {
  search: string;
  onlyIssues: boolean;
}

export const filterEmergencyMonitoringSessions = (
  sessions: EmergencyMonitoringSession[],
  filters: EmergencyMonitoringFilters,
  nowMs: number,
): EmergencyMonitoringSession[] => {
  const normalizedSearch = filters.search.trim().toLocaleLowerCase();
  return sessions.filter(session => {
    if (filters.onlyIssues && !hasEmergencyMonitoringIssue(session, nowMs)) return false;
    if (!normalizedSearch) return true;
    return [session.workerName, session.emergencyTypeName].some(
      value => typeof value === 'string' && value.toLocaleLowerCase().includes(normalizedSearch),
    );
  });
};

const MONITORING_STAGE_RANK: Record<EmergencyMonitoringWarningStage, number> = {
  overdue: 0,
  fullScreen: 1,
  standard: 2,
  normal: 3,
};

/**
 * Triage order for the watchboard: the worker closest to becoming an emergency
 * sits at the top. Warning stage leads because it is the escalation the system
 * is actually counting down to, and the emergency deadline orders the rest. Falls back to id so the order never
 * depends on document arrival.
 */
export const compareEmergencyMonitoringSessions = (
  first: EmergencyMonitoringSession,
  second: EmergencyMonitoringSession,
  nowMs: number,
): number => {
  const stageDelta =
    MONITORING_STAGE_RANK[getEmergencyMonitoringWarningStage(first, nowMs)] -
    MONITORING_STAGE_RANK[getEmergencyMonitoringWarningStage(second, nowMs)];
  if (stageDelta !== 0) return stageDelta;

  const deadlineDelta =
    (DateUtil.getTimestampMs(first.emergencyAt) ?? Number.MAX_SAFE_INTEGER) -
    (DateUtil.getTimestampMs(second.emergencyAt) ?? Number.MAX_SAFE_INTEGER);
  if (deadlineDelta !== 0) return deadlineDelta;

  return first.id.localeCompare(second.id);
};

export const sortEmergencyMonitoringSessions = (
  sessions: EmergencyMonitoringSession[],
  nowMs: number,
): EmergencyMonitoringSession[] =>
  [...sessions].sort((first, second) => compareEmergencyMonitoringSessions(first, second, nowMs));

export const getEmergencyElapsedSeconds = (
  incident: EmergencyIncident,
  nowMs: number,
): number => {
  const activatedAt = DateUtil.getTimestampMs(incident.activatedAt);
  return activatedAt === null ? 0 : Math.max(0, Math.floor((nowMs - activatedAt) / 1000));
};

export const getEmergencyElapsedMinutes = (incident: EmergencyIncident, nowMs: number): number =>
  Math.floor(getEmergencyElapsedSeconds(incident, nowMs) / 60);

/**
 * Seconds until the next escalation deadline. Negative once the deadline has passed,
 * null when the incident has no pending deadline.
 */
export const getEmergencyDeadlineSeconds = (
  incident: EmergencyIncident,
  nowMs: number,
): number | null => {
  const deadlineMs = DateUtil.getTimestampMs(incident.escalation.nextDeadlineAt);
  return deadlineMs === null ? null : Math.round((deadlineMs - nowMs) / 1000);
};

/** Formats a non-negative duration as m:ss, or h:mm:ss once it passes an hour. */
export const formatEmergencyDuration = (totalSeconds: number): string => {
  const safeSeconds = Math.max(0, Math.floor(totalSeconds));
  const hours = Math.floor(safeSeconds / 3600);
  const minutes = Math.floor((safeSeconds % 3600) / 60);
  const seconds = safeSeconds % 60;
  const paddedSeconds = String(seconds).padStart(2, '0');
  if (hours > 0) return `${hours}:${String(minutes).padStart(2, '0')}:${paddedSeconds}`;
  return `${minutes}:${paddedSeconds}`;
};

export const getEmergencyFreshnessState = (
  incident: EmergencyIncident,
  nowMs: number,
  location?: EmergencyLocation,
): EmergencyFreshnessState => {
  if (incident.freshness.stale) return 'stale';
  const activityMs = DateUtil.getTimestampMs(incident.freshness.lastActivityAt) ?? 0;
  const capturedMs = DateUtil.getTimestampMs(location?.capturedAt) ?? 0;
  const receivedMs = DateUtil.getTimestampMs(location?.receivedAt) ?? 0;
  const latestSignalMs = Math.max(activityMs, capturedMs, receivedMs);
  return getFreshnessState(latestSignalMs, nowMs);
};

export const getEmergencyLocationFreshnessState = (
  location: EmergencyLocation | undefined,
  nowMs: number,
): EmergencyFreshnessState => {
  const capturedMs = DateUtil.getTimestampMs(location?.capturedAt) ?? 0;
  const receivedMs = DateUtil.getTimestampMs(location?.receivedAt) ?? 0;
  return getFreshnessState(capturedMs || receivedMs, nowMs);
};

const getFreshnessState = (latestSignalMs: number, nowMs: number): EmergencyFreshnessState => {
  if (!latestSignalMs) return 'stale';
  const ageMinutes = Math.max(0, (nowMs - latestSignalMs) / 60000);
  if (ageMinutes >= 10) return 'stale';
  if (ageMinutes >= 5) return 'warning';
  return 'fresh';
};

export const getEmergencyDeadlineState = (
  incident: EmergencyIncident,
  nowMs: number,
): EmergencyDeadlineState => {
  const deadlineMs = DateUtil.getTimestampMs(incident.escalation.nextDeadlineAt);
  if (deadlineMs === null) return 'none';
  return deadlineMs <= nowMs ? 'due' : 'upcoming';
};

/**
 * Triage order for the command centre rail: unassigned first, then overdue
 * escalations, then the nearest next deadline, then longest elapsed. Operators
 * work the top of the list downwards, so the ordering has to be stable and
 * never depend on document arrival order.
 */
export const compareEmergencyIncidents = (
  first: EmergencyIncident,
  second: EmergencyIncident,
  nowMs: number,
): number => {
  const firstUnassigned = hasAssignedResponder(first) ? 0 : 1;
  const secondUnassigned = hasAssignedResponder(second) ? 0 : 1;
  if (firstUnassigned !== secondUnassigned) return secondUnassigned - firstUnassigned;

  const firstOverdue = getEmergencyDeadlineState(first, nowMs) === 'due' ? 1 : 0;
  const secondOverdue = getEmergencyDeadlineState(second, nowMs) === 'due' ? 1 : 0;
  if (firstOverdue !== secondOverdue) return secondOverdue - firstOverdue;

  const firstDeadline = DateUtil.getTimestampMs(first.escalation.nextDeadlineAt) ?? Number.MAX_SAFE_INTEGER;
  const secondDeadline = DateUtil.getTimestampMs(second.escalation.nextDeadlineAt) ?? Number.MAX_SAFE_INTEGER;
  if (firstDeadline !== secondDeadline) return firstDeadline - secondDeadline;

  const elapsedDelta =
    getEmergencyElapsedSeconds(second, nowMs) - getEmergencyElapsedSeconds(first, nowMs);
  if (elapsedDelta !== 0) return elapsedDelta;

  return first.id.localeCompare(second.id);
};

export const sortEmergencyIncidents = (
  incidents: EmergencyIncident[],
  nowMs: number,
): EmergencyIncident[] =>
  [...incidents].sort((first, second) => compareEmergencyIncidents(first, second, nowMs));

export const countOverdueEmergencies = (incidents: EmergencyIncident[], nowMs: number): number =>
  incidents.filter(incident => getEmergencyDeadlineState(incident, nowMs) === 'due').length;

/**
 * Whether a primary responder has accepted this incident.
 *
 * Invited responders alone do not count — assignment starts as `invited` and
 * only `acceptPrimary` sets `primary` / `primaryAssignmentId`.
 *
 * The active-list subscription now loads assignment records, so `response.primary`
 * is usually present. `primaryAssignmentId` remains a fallback for snapshots
 * that have accepted a primary but have not yet delivered the assignment docs.
 */
export const hasAssignedResponder = (incident: EmergencyIncident): boolean => {
  const subjectUserId = incident.subject.kind === 'linkedWorker'
    ? incident.subject.userId
    : undefined;
  if (subjectUserId && incident.response.primary?.userId === subjectUserId) return false;
  return Boolean(incident.response.primary || incident.primaryAssignmentId);
};

const TERMINAL_RESPONDER_STATES = new Set<EmergencyResponderState>([
  'reassigned',
  'released',
]);

export const isOpenEmergencyResponderState = (state: EmergencyResponderState): boolean =>
  !TERMINAL_RESPONDER_STATES.has(state);

/**
 * FCM delivery (Queued → Accepted) is only meaningful for an invite still in
 * flight. Assigned and former assignees would otherwise look like they finished
 * the response because operator-assign receipts are stored as `accepted`.
 */
export const shouldShowEmergencyNotificationChain = (
  responder: Pick<EmergencyResponder, 'primary' | 'state'>,
): boolean => responder.state === 'invited' && !responder.primary;

export const getEmergencyResponderRoleMessageId = (
  responder: Pick<EmergencyResponder, 'primary' | 'state'>,
): string => {
  if (TERMINAL_RESPONDER_STATES.has(responder.state)) {
    return 'emergencies.responderState.reassigned';
  }
  if (responder.primary) {
    return 'emergencies.responder.primary';
  }
  return `emergencies.responderState.${responder.state}`;
};

export const getEmergencyResponderProgressMessageId = (
  responder: Pick<EmergencyResponder, 'primary' | 'state'>,
): string | undefined => {
  if (
    !responder.primary ||
    !isOpenEmergencyResponderState(responder.state) ||
    responder.state === 'accepted'
  ) {
    return undefined;
  }
  return `emergencies.responderState.${responder.state}`;
};

/**
 * Assignment slot to replace when the operator still has to use `reassign`
 * (older backends). Prefers the accepted primary, then any active invite.
 */
export const getReplaceableAssignmentId = (incident: EmergencyIncident): string | undefined =>
  incident.response.primary?.assignmentId ||
  incident.primaryAssignmentId ||
  incident.response.support.find(
    responder => responder.state !== 'reassigned' && responder.state !== 'released',
  )?.assignmentId;

export const countUnassignedEmergencies = (incidents: EmergencyIncident[]): number =>
  incidents.filter(incident => !hasAssignedResponder(incident)).length;

/**
 * Backend stores level 0 before the first escalation fires. Operators should see
 * that as "pending" rather than a literal Level 0.
 */
export const getEmergencyEscalationLevelLabelValues = (
  currentLevel: number,
): { pending: boolean; level: number } => ({
  pending: currentLevel <= 0,
  level: Math.max(1, currentLevel),
});

export const isEmergencyIncidentRelevant = (
  incident: EmergencyIncident,
  currentUserId: string,
  canViewDivision: boolean,
): boolean => {
  if (canViewDivision) return true;
  if (incident.subject.kind === 'linkedWorker' && incident.subject.userId === currentUserId) {
    return true;
  }
  return [
    ...(incident.response.primary ? [incident.response.primary] : []),
    ...incident.response.support,
  ].some(responder => responder.userId === currentUserId);
};

export const filterEmergencyIncidents = (
  incidents: EmergencyIncident[],
  filters: EmergencyIncidentFilters,
  nowMs = 0,
): EmergencyIncident[] => {
  const normalizedSearch = filters.search.trim().toLocaleLowerCase();
  return incidents.filter(incident => {
    if (filters.divisionIds?.length && !filters.divisionIds.includes(incident.divisionId)) {
      return false;
    }
    if (filters.urgency === 'unassigned' && hasAssignedResponder(incident)) return false;
    if (filters.urgency === 'overdue' && getEmergencyDeadlineState(incident, nowMs) !== 'due') {
      return false;
    }
    if (!normalizedSearch) return true;
    // Any of these may be absent on partially-populated incidents, so never
    // call a string method without narrowing first.
    const searchableValues = [
      incident.id,
      incident.emergencyTypeName,
      incident.subject.displayName,
      incident.source?.label,
      incident.response.primary?.displayName,
    ];
    return searchableValues.some(
      value => typeof value === 'string' && value.toLocaleLowerCase().includes(normalizedSearch),
    );
  });
};

export interface EmergencyContextEntry {
  label: string;
  value: string;
}

const stringifyContextValue = (value: unknown): string => {
  if (value == null) return '';
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  try {
    return JSON.stringify(value);
  } catch {
    return '';
  }
};

/**
 * Turns a free-form context string into labelled rows. JSON objects become
 * key/value pairs; anything else is shown as a single unlabelled value.
 */
export const getEmergencyContextEntries = (value: string | undefined): EmergencyContextEntry[] => {
  if (!value?.trim()) return [];
  const trimmed = value.trim();
  try {
    const parsed: unknown = JSON.parse(trimmed);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return Object.entries(parsed as Record<string, unknown>)
        .map(([label, entryValue]) => ({ label, value: stringifyContextValue(entryValue) }))
        .filter(entry => entry.value);
    }
    const serialized = stringifyContextValue(parsed);
    return serialized ? [{ label: '', value: serialized }] : [{ label: '', value: trimmed }];
  } catch {
    return [{ label: '', value: trimmed }];
  }
};

/**
 * Latest receipt for a person. Escalation can queue several; the operator
 * needs the current attempt, not the first invite that was superseded.
 */
export const getLatestEmergencyNotification = (
  notifications: EmergencyNotification[],
  recipientId: string,
): EmergencyNotification | undefined => {
  const matches = notifications.filter(notification => notification.recipientId === recipientId);
  if (!matches.length) return undefined;
  return matches.reduce((latest, current) =>
    current.escalationLevel >= latest.escalationLevel ? current : latest,
  );
};

/**
 * Receipts whose recipient is not already on the people list (subject or
 * responders). Shown as extra rows so a notified person never disappears.
 */
export const listUnmatchedEmergencyNotifications = (
  notifications: EmergencyNotification[],
  participantIds: string[],
): EmergencyNotification[] => {
  const knownIds = new Set(participantIds.filter(Boolean));
  const seen = new Set<string>();
  const unmatched: EmergencyNotification[] = [];
  notifications.forEach(notification => {
    if (knownIds.has(notification.recipientId) || seen.has(notification.recipientId)) return;
    seen.add(notification.recipientId);
    const latest = getLatestEmergencyNotification(notifications, notification.recipientId);
    if (latest) unmatched.push(latest);
  });
  return unmatched;
};

export const EMERGENCY_NOTIFICATION_PROGRESS_STEPS = [
  { key: 'queuedStatus', at: 'queuedAt', labelId: 'emergencies.notification.queued' },
  { key: 'providerStatus', at: 'sentAt', labelId: 'emergencies.notification.provider' },
  { key: 'deliveryStatus', at: 'deliveredAt', labelId: 'emergencies.notification.delivery' },
  { key: 'viewStatus', at: 'viewedAt', labelId: 'emergencies.notification.viewed' },
  { key: 'acceptanceStatus', at: 'acceptedAt', labelId: 'emergencies.notification.accepted' },
] as const;

/**
 * Latest delivery milestone that is no longer pending. Operators need one
 * word, not the full five-step chain, when scanning a crowded roster.
 */
export const getEmergencyNotificationProgressLabelId = (
  item: EmergencyNotification,
): string => {
  const reached = [...EMERGENCY_NOTIFICATION_PROGRESS_STEPS]
    .reverse()
    .find(step => item[step.key] !== 'pending');
  return reached?.labelId ?? 'emergencies.notification.none';
};

export interface EmergencyRosterEntry {
  key: string;
  userId: string;
  displayName: string;
  responder?: EmergencyResponder;
  notification?: EmergencyNotification;
}

export interface EmergencyRosterInviteGroup {
  level: number;
  entries: EmergencyRosterEntry[];
}

export interface EmergencyPeopleRoster {
  responsible: EmergencyRosterEntry[];
  support: EmergencyRosterEntry[];
  invitedGroups: EmergencyRosterInviteGroup[];
  former: EmergencyRosterEntry[];
}

const toRosterEntry = (
  responder: EmergencyResponder,
  notifications: EmergencyNotification[],
  fallbackName: string,
): EmergencyRosterEntry => ({
  key: responder.assignmentId,
  userId: responder.userId,
  displayName: responder.displayName || fallbackName,
  responder,
  notification: getLatestEmergencyNotification(notifications, responder.userId),
});

const getRosterProgressRank = (entry: EmergencyRosterEntry): number => {
  const responderRank: Record<string, number> = {
    accepted: 5,
    joined: 6,
    enRoute: 7,
    arrived: 8,
  };
  const notification = entry.notification;
  const notificationRank = notification?.acceptanceStatus === 'accepted' || notification?.status === 'accepted'
    ? 5
    : notification?.viewStatus === 'viewed' || notification?.status === 'viewed'
      ? 4
      : notification?.deliveryStatus === 'delivered' || notification?.status === 'delivered'
        ? 3
        : notification?.providerStatus === 'sent' || notification?.status === 'sent' || notification?.sentAt
          ? 2
          : notification?.providerStatus === 'providerAccepted' || notification?.status === 'providerAccepted'
            ? 1
            : 0;
  return Math.max(
    entry.responder ? responderRank[entry.responder.state] ?? 0 : 0,
    notificationRank,
  );
};

const sortRosterEntriesByProgress = (
  entries: EmergencyRosterEntry[],
): EmergencyRosterEntry[] => entries.sort(
  (first, second) => getRosterProgressRank(second) - getRosterProgressRank(first),
);

/**
 * Splits the people list into the roles an operator actually acts on:
 * the person in charge, helpers already on the job, invites still in flight
 * (grouped by escalation level so "Level N" is not repeated on every row),
 * and people who are no longer assigned.
 */
export const buildEmergencyPeopleRoster = (
  responders: EmergencyResponder[],
  notifications: EmergencyNotification[],
  unmatchedNotifications: EmergencyNotification[],
  fallbackName: string,
): EmergencyPeopleRoster => {
  const responsible: EmergencyRosterEntry[] = [];
  const support: EmergencyRosterEntry[] = [];
  const former: EmergencyRosterEntry[] = [];
  const invitedByLevel = new Map<number, EmergencyRosterEntry[]>();

  const pushInvited = (entry: EmergencyRosterEntry, level: number) => {
    const group = invitedByLevel.get(level);
    if (group) {
      group.push(entry);
      return;
    }
    invitedByLevel.set(level, [entry]);
  };

  responders.forEach(responder => {
    const entry = toRosterEntry(responder, notifications, fallbackName);
    if (TERMINAL_RESPONDER_STATES.has(responder.state)) {
      former.push(entry);
      return;
    }
    if (responder.primary) {
      responsible.push(entry);
      return;
    }
    if (responder.state === 'invited') {
      pushInvited(entry, entry.notification?.escalationLevel ?? 0);
      return;
    }
    support.push(entry);
  });

  unmatchedNotifications.forEach(item => {
    pushInvited(
      {
        key: item.id,
        userId: item.recipientId,
        displayName: item.recipientName || fallbackName,
        notification: item,
      },
      item.escalationLevel,
    );
  });

  return {
    responsible,
    support: sortRosterEntriesByProgress(support),
    invitedGroups: Array.from(invitedByLevel.entries())
      .sort((first, second) => second[0] - first[0])
      .map(([level, entries]) => ({ level, entries: sortRosterEntriesByProgress(entries) })),
    former,
  };
};

/**
 * Collapse keys that should start open: people already helping, plus the
 * invite wave that matches the incident's current escalation (or the latest
 * wave if that level has no one yet). Former assignees stay closed.
 */
export const getEmergencyRosterOpenKeys = (
  roster: EmergencyPeopleRoster,
  currentLevel: number,
): string[] => {
  const keys: string[] = [];
  if (roster.support.length) keys.push('support');
  const currentInvited = roster.invitedGroups.find(group => group.level === currentLevel);
  const openInvited = currentInvited ?? roster.invitedGroups[0];
  if (openInvited) keys.push(`invited-${openInvited.level}`);
  return keys;
};

export type EmergencyMapParticipantRole = 'subject' | 'assigned' | 'potential';

export interface EmergencyMapParticipant {
  userId: string;
  displayName: string;
  role: EmergencyMapParticipantRole;
  location?: EmergencyLocation;
  /** Profile photo for the map marker; initials are used when absent or broken. */
  photoURL?: string;
}

/** Up to two initials from a display name, for avatar fallbacks. */
export const getEmergencyInitials = (name: string): string =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part[0])
    .join('')
    .toUpperCase() || '?';

const pushMapParticipant = (
  participants: EmergencyMapParticipant[],
  seen: Set<string>,
  entry: EmergencyMapParticipant,
): void => {
  if (!entry.userId || seen.has(entry.userId)) return;
  seen.add(entry.userId);
  participants.push(entry);
};

/**
 * People the selected-incident map should know about: the person in emergency,
 * assigned responders, and invites still in flight. Former assignees stay off
 * the map. Location is optional — the map plots pings and counts the rest.
 */
export const buildEmergencyMapParticipants = (
  subjectUserId: string | null,
  subjectDisplayName: string,
  roster: EmergencyPeopleRoster,
  locationsByUser: Record<string, EmergencyLocation>,
): EmergencyMapParticipant[] => {
  const participants: EmergencyMapParticipant[] = [];
  const seen = new Set<string>();

  if (subjectUserId) {
    pushMapParticipant(participants, seen, {
      userId: subjectUserId,
      displayName: subjectDisplayName,
      role: 'subject',
      location: locationsByUser[subjectUserId],
    });
  } else if (subjectDisplayName) {
    participants.push({
      userId: '',
      displayName: subjectDisplayName,
      role: 'subject',
    });
  }

  roster.responsible.forEach(entry => {
    pushMapParticipant(participants, seen, {
      userId: entry.userId,
      displayName: entry.displayName,
      role: 'assigned',
      location: locationsByUser[entry.userId],
    });
  });
  roster.support.forEach(entry => {
    pushMapParticipant(participants, seen, {
      userId: entry.userId,
      displayName: entry.displayName,
      role: 'assigned',
      location: locationsByUser[entry.userId],
    });
  });
  roster.invitedGroups.forEach(group => {
    group.entries.forEach(entry => {
      pushMapParticipant(participants, seen, {
        userId: entry.userId,
        displayName: entry.displayName,
        role: 'potential',
        location: locationsByUser[entry.userId],
      });
    });
  });

  return participants;
};

/**
 * The command centre already has the incident's latest ping before the detail
 * stream catches up. Use it only when the subject has no position of their own.
 */
export const withEmergencyMapSubjectFallback = (
  participants: EmergencyMapParticipant[],
  fallback: EmergencyLocation | undefined,
  fallbackName: string,
): EmergencyMapParticipant[] => {
  if (!fallback) return participants;
  const subjectIndex = participants.findIndex(participant => participant.role === 'subject');
  if (subjectIndex === -1) {
    return [
      {
        userId: fallback.userId,
        displayName: fallbackName,
        role: 'subject',
        location: fallback,
      },
      ...participants,
    ];
  }
  if (participants[subjectIndex].location) return participants;
  return participants.map((participant, index) =>
    index === subjectIndex ? { ...participant, location: fallback } : participant,
  );
};

export interface EmergencyIncidentMapParticipant extends EmergencyMapParticipant {
  incidentId: string;
  emergencyTypeName?: string;
}

/** Only attribute a position to its actual user; responder pings must never move a subject. */
export const buildEmergencyCommandMapParticipants = ({
  incidents,
  selectedIncidentId,
  participants,
  locationsByIncident,
  unknownSubjectName,
}: {
  incidents: EmergencyIncident[];
  selectedIncidentId: string | null;
  participants: EmergencyMapParticipant[];
  locationsByIncident: Record<string, EmergencyLocation | undefined>;
  unknownSubjectName: string;
}): EmergencyIncidentMapParticipant[] => {
  const selectedIncident = incidents.find(incident => incident.id === selectedIncidentId);
  const fallback = selectedIncident ? locationsByIncident[selectedIncident.id] : undefined;
  const subjectUserId = selectedIncident?.subject.kind === 'linkedWorker'
    ? selectedIncident.subject.userId
    : undefined;
  const selected = selectedIncident ? withEmergencyMapSubjectFallback(
    participants,
    fallback && fallback.userId === subjectUserId ? fallback : undefined,
    selectedIncident.subject.displayName || unknownSubjectName,
  ) : [];
  const subjects = incidents.map<EmergencyIncidentMapParticipant>(incident => {
    const userId = incident.subject.kind === 'linkedWorker' ? incident.subject.userId : '';
    const latest = locationsByIncident[incident.id];
    const selectedSubject = incident.id === selectedIncidentId
      ? selected.find(participant => participant.role === 'subject' && participant.userId === userId)
      : undefined;
    return {
      userId,
      displayName: incident.subject.displayName || unknownSubjectName,
      role: 'subject',
      location: selectedSubject?.location ?? (userId && latest?.userId === userId ? latest : undefined),
      photoURL: selectedSubject?.photoURL,
      incidentId: incident.id,
      emergencyTypeName: incident.emergencyTypeName,
    };
  });
  const subjectIds = new Set(subjects.map(subject => subject.userId));
  const responders = selected.filter(participant =>
    participant.role !== 'subject' && !subjectIds.has(participant.userId),
  );
  return [
    ...subjects,
    ...responders.map(participant => ({ ...participant, incidentId: selectedIncident!.id })),
  ];
};

export const getEmergencyMapPointSignature = (participants: EmergencyMapParticipant[]): string =>
  participants
    .filter(participant => participant.location)
    .map(participant => participant.userId)
    .sort()
    .join('|');

export const countEmergencyMapMissingLocations = (
  participants: EmergencyMapParticipant[],
): number => participants.filter(participant => !participant.location).length;

/** Keep the Leaflet world at least as large as the viewport at minimum zoom. */
export const getEmergencyMapMinimumZoom = (
  width: number,
  height: number,
  tileSize = 256,
): number => Math.max(0, Math.ceil(Math.log2(Math.max(width, height) / tileSize)));

/**
 * The operator owns the camera after they pan or zoom. Auto-frame only when
 * the situation itself changes (new incident, new people with pings) or when
 * they ask to see everyone again. GPS ticks must never move the map.
 */
export const shouldFrameEmergencyMap = ({
  hasPoints,
  incidentChanged,
  incidentClosed = false,
  pointSetChanged,
  operatorMoved,
  forceFrame,
}: {
  hasPoints: boolean;
  incidentChanged: boolean;
  incidentClosed?: boolean;
  pointSetChanged: boolean;
  operatorMoved: boolean;
  forceFrame: boolean;
}): boolean => {
  if (incidentClosed) return false;
  if (!hasPoints) return false;
  if (forceFrame || incidentChanged) return true;
  if (operatorMoved) return false;
  return pointSetChanged;
};

export interface EmergencyThreadMessage {
  entry: EmergencyTimelineEntry;
  isOwn: boolean;
  showAuthor: boolean;
}

const getTimelineEntryMs = (entry: EmergencyTimelineEntry): number =>
  DateUtil.getTimestampMs(entry.createdAt) ?? 0;

/**
 * Operator notes as a chat thread: oldest first so the conversation reads
 * downward toward the latest message, with a flag for the signed-in operator.
 */
export const presentEmergencyThreadMessages = (
  timeline: EmergencyTimelineEntry[],
  currentUserId: string,
): EmergencyThreadMessage[] => {
  const messages = timeline
    .filter(entry => entry.type === EMERGENCY_TIMELINE_TYPES.NOTE_ADDED)
    .slice()
    .sort((first, second) => getTimelineEntryMs(first) - getTimelineEntryMs(second));

  return messages.map((entry, index) => {
    const previous = messages[index - 1];
    const isOwn = Boolean(currentUserId) && entry.actorId === currentUserId;
    return {
      entry,
      isOwn,
      showAuthor: !previous || previous.actorId !== entry.actorId,
    };
  });
};
